import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import siteSettingsService from '../../services/siteSettingsService';
import { initEmailJS, sendAppointmentConfirmationEmail } from '../../services/emailService';

/**
 * Component to display when an appointment is successfully booked
 * Also handles sending confirmation email to the user
 */
const SuccessView = ({ 
  formData, 
  practice, 
  selectedSlot, 
  nextAvailableDates,
  formatDateFull,
  formatTime,
  getTodayDateString,
  onReturnHome 
}) => {
  const [useImmediatePaymentProcessing, setUseImmediatePaymentProcessing] = useState(false);
  
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  // Fetch payment processing setting on component mount and initialize EmailJS
  useEffect(() => {
    // Initialize EmailJS
    initEmailJS();
    
    const fetchPaymentProcessingSetting = async () => {
      try {
        const settings = await siteSettingsService.getSettings();
        setUseImmediatePaymentProcessing(settings.useImmediatePaymentProcessing || false);
      } catch (error) {
        console.error('Error fetching payment processing setting:', error);
      }
    };
    
    fetchPaymentProcessingSetting();
  }, []);
  // Debug check to ensure nextAvailableDates is valid and send confirmation email
  useEffect(() => {
    console.log('SuccessView - nextAvailableDates:', nextAvailableDates);
    console.log('SuccessView - practice:', practice);
    
    if (!nextAvailableDates) {
      console.error('nextAvailableDates is null or undefined');
    }
    
    if (!practice) {
      console.error('practice is null or undefined');
    }

    // Send confirmation email if we have all the required data
    const sendConfirmationEmail = async () => {
      if (formData && formData.email && practice && selectedSlot) {
        try {
          // Prepare appointment data for email
          const appointmentData = {
            id: selectedSlot.id,
            date: getAppointmentDate(),
            practiceName: practice.name,
            practiceAddress: practice.address ? 
              `${practice.address.line1}${practice.address.line2 ? `, ${practice.address.line2}` : ''}, ${practice.address.city}, ${practice.address.postcode}` : 
              'Address not available',
            practicePhone: practice.phone || 'Not available',
            slot: {
              startTime: selectedSlot.startTime,
              endTime: selectedSlot.endTime
            },
            userInfo: {
              name: formData.name,
              phone: formData.phone,
              postcode: formData.postcode,
              issue: formData.issue || ''
            }
          };

          // Send the confirmation email
          await sendAppointmentConfirmationEmail(appointmentData, formData.email);
          setEmailSent(true);
          console.log('Confirmation email sent successfully');
        } catch (error) {
          console.error('Error sending confirmation email:', error);
          setEmailError('Failed to send confirmation email. Please check your email address.');
        }
      } else {
        console.warn('Missing data required for sending confirmation email');
      }
    };

    sendConfirmationEmail();
  }, [nextAvailableDates, practice, formData, selectedSlot]);
  // Get the date for the selected slot
  const getAppointmentDate = () => {
    if (practice && nextAvailableDates && nextAvailableDates[practice.id]) {
      return nextAvailableDates[practice.id].date;
    }
    return getTodayDateString();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="bg-green-100 px-6 py-4 border-b border-green-200">
        <h2 className="text-2xl font-bold text-green-800">
          Appointment Booked Successfully!
        </h2>
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-green-100 rounded-full p-3">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-12 w-12 text-green-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
        </div>
        
        <p className="text-center text-gray-700 mb-6">
          Thank you for booking with Dental Hero. 
          {emailSent ? 
            " We've sent a confirmation email to your registered email address." : 
            " We're preparing your confirmation email."}
        </p>
        
        {emailError && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
            <p className="text-sm text-yellow-700">
              {emailError} You can still view your appointment details below.
            </p>
          </div>
        )}
        
        {!useImmediatePaymentProcessing && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
            <h3 className="text-md font-semibold text-yellow-800 mb-2">
              Payment Status
            </h3>
            <p className="text-sm text-yellow-700">
              Your payment has been authorized but will not be processed until your appointment is confirmed by the dental practice. You will receive a confirmation email once your appointment is confirmed.
            </p>
          </div>
        )}
        
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">
            Your Appointment Details
          </h3>
          
          <div className="space-y-3">
            <p><strong>Name:</strong> {formData.name}</p>
            <p><strong>Phone:</strong> {formData.phone}</p>
            <p><strong>Postcode:</strong> {formData.postcode}</p>
            <p><strong>Date:</strong> {formatDateFull(getAppointmentDate())}</p>
            
            {selectedSlot && (
              <p>
                <strong>Time:</strong> {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}
              </p>
            )}
            
            <div className="pt-2">
              <h4 className="font-semibold text-blue-800 mb-1">Practice Information</h4>
              <p><strong>Name:</strong> {practice.name}</p>
              <p>
                <strong>Address:</strong> {practice.address?.line1}
                {practice.address?.line2 ? `, ${practice.address.line2}` : ''}, 
                {practice.address?.city}, {practice.address?.postcode}
              </p>
              <p><strong>Phone:</strong> {practice.phone || 'Not available'}</p>
              {practice.distance && (
                <p><strong>Distance:</strong> {practice.distance} km from your location</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            What to Bring
          </h3>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            <li>Photo ID (passport, driving license)</li>
            <li>Any relevant medical history or documentation</li>
            <li>Your NHS number (if applicable)</li>
            <li>Details of any medications you're currently taking</li>
            <li>Your payment method (credit/debit card, cash)</li>
          </ul>
        </div>
        
        <div className="flex flex-col space-y-4">
          <button
            onClick={onReturnHome}
            className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded transition-colors"
          >
            Return to Home
          </button>
          
          <Link
            to="/user-dashboard"
            className="text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-6 rounded transition-colors"
          >
            View My Appointments
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuccessView;
